import { useEffect, useRef, useState } from "react";
import axios from "axios";
import EventEHRLazyDropdown from "../../Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import { getToken } from "../../../utils/getToken";
import { bottomDirectionArrow } from "../../../assets";

interface FacilityOption {
  label: string;
  value: string;
  department: any[];
}

interface FacilitySelectProps {
  // The currently selected facility option ({ label, value, department }) or
  // null. Owned by the parent so the selection persists across open/close.
  selectedValue: FacilityOption | null;
  // Called with the selected facility option (or null on clear). The parent
  // reads `department` from it to build the Type of Care list.
  onSelect: (facility: FacilityOption | null) => void;
  error?: boolean;
  helperText?: string;
  isDisabled?: boolean;
}

/**
 * Facility selector that lazy-loads the facility list on scroll, mirroring the
 * ICD-10/DSM `DiagnosisCodeSelect` pattern. It owns its own pagination state and
 * uses the shared `EventEHRLazyDropdown`; typing filters the loaded options
 * client-side (MUI default) while scrolling to the bottom loads the next page.
 */
const FacilitySelect = ({
  selectedValue,
  onSelect,
  error = false,
  helperText,
  isDisabled = false,
}: FacilitySelectProps) => {
  const [pageCount, setPageCount] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState<FacilityOption[]>([]);
  const [resetFlag, setResetFlag] = useState(false);
  // Whether there are more pages to load. Read synchronously (ref, not state)
  // so the scroll-driven pageCount bump can't trigger a fetch past the last
  // page. Without this the dropdown fetches page 2 on open even when the
  // response reports a single page.
  const hasMoreRef = useRef(true);

  // Browse the facility list, paginated (appends on scroll). The resetFlag
  // guard mirrors DiagnosisCodeSelect: while a reset is in flight this effect
  // skips so we don't double-fetch page 1.
  useEffect(() => {
    if (!resetFlag) {
      getDropdownList(pageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  useEffect(() => {
    if (resetFlag) {
      hasMoreRef.current = true;
      setPageCount(1);
      getDropdownList(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFlag]);

  const mapFacilities = (facilitys: any[]): FacilityOption[] =>
    facilitys?.map((facility: any) => ({
      label: facility?.name,
      value: facility?.id,
      department: facility?.department ?? [],
    })) || [];

  const getDropdownList = async (page: any, reset = false) => {
    // Don't fetch past the last page (the scroll handler may still bump
    // pageCount once we're at the bottom of a single-page list).
    if (!reset && page > 1 && !hasMoreRef.current) {
      return;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/facility`,
        {
          params: { status: "Active", pageNumber: page, limit: 100 },
          headers: {
            Authorization: `Bearer ${JSON.parse(getToken() || "")}`,
          },
        }
      );
      const facilitys = response?.data?.facilitys || [];
      hasMoreRef.current = page < (response?.data?.totalPages ?? 1);
      const options = mapFacilities(facilitys);
      if (options.length > 0) {
        if (reset) {
          setDropdownOptions(options);
          setResetFlag(false);
        } else if (
          !dropdownOptions?.some((o) => o?.value === options[0]?.value)
        ) {
          // Append the next page (dedupe by checking the first new row).
          setDropdownOptions(dropdownOptions.concat(options));
        }
      } else if (reset) {
        setResetFlag(false);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <EventEHRLazyDropdown
      label="Select Facility"
      placeholder="Select Facility"
      isDisabled={isDisabled}
      error={error}
      helperText={helperText}
      noOptionText="No facility found"
      dropdownOptions={dropdownOptions}
      dropdownSearch={dropdownSearch}
      selectedValue={selectedValue}
      fullWidth="100%"
      dropdownIcon={true}
      endIcon={
        <img
          alt="dropdown-arrow"
          src={bottomDirectionArrow}
          width={"14px"}
          height={"14px"}
        />
      }
      setDropdownSearch={setDropdownSearch}
      setPageCount={setPageCount}
      setResetFlag={setResetFlag}
      setSelectedValue={(data) => {
        onSelect(data);
      }}
    />
  );
};

export default FacilitySelect;
